import * as Sentry from "@sentry/browser"
import {api, apiErrorMessage} from "js/studios/shared/api"

function reviewsErr(errMsg) {
  const p = document.createElement("p")
  p.classList.add("txt--center")
  p.classList.add("content--75pct")
  const b = document.createElement("b")
  p.append(b)
  b.innerText = errMsg || "Sorry, an error prevented displaying reviews right now."

  return p
}

class Stars {
  static {
    this.tmpl = document.createElement("template")
    this.tmpl.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 22"><path d="m12 0 2.694 8.292h8.719l-7.054 5.124 2.694 8.292L12 16.584l-7.053 5.124 2.694-8.292L.587 8.292h8.719L12 0Z"/></svg>
    `
  }

  constructor(starCount) {
    this.starCount = starCount
  }

  render() {
    const wrapper = document.createElement("div")
    wrapper.classList.add("star-rating")
    for (let i = 0; i < this.starCount; i++) {
      wrapper.appendChild(this.constructor.tmpl.content.querySelector("svg").cloneNode(true))
    }
    return wrapper
  }
}

class Review {
  static toggleText = "Continue reading"
  static subScores = [
    {attr: "timelinessScore", title: "Timeliness"},
    {attr: "qualityScore", title: "Expertise"},
    {attr: "communicationScore", title: "Communication"},
    {attr: "shippingScore", title: "Shipping/Delivery"},
    {attr: "professionalismScore", title: "Professionalism"},
    {attr: "valueScore", title: "Value"},
  ]

  static {
      this.tmpl = document.createElement("template")
      this.tmpl.innerHTML = `
      <div class="category-review">
        <div class="category-review__project">
          <img data-field="customer-avatar"/>
          <span class="txt--body-1" data-field="customer-name"><b></b></span>
          <div data-field="rating"></div>
          <span class="txt--body-2" data-field="project-name"></span>
          <span class="txt--caption" data-field="date"></span>
        </div>
        <div class="category-review__body">
          <div class="category-review__photos"></div>
          <div class="category-review__details txt--body-2">
            <div class="category-review__scores"></div>
          </div>
          <a href="#" class="txt--menu-2" data-action="toggle">${this.toggleText}</a>
        </div>
      </div>
      `

      this.dateFormatter = new Intl.DateTimeFormat([], {
        year: '2-digit', month: '2-digit', day: '2-digit',
      })
  }

  static fmtUnix(unixTime) {
    const date = new Date(unixTime * 1000)
    return this.dateFormatter.format(date)
  }

  constructor(review) {
    this.review = review
  }

  onToggle = ev => {
    ev.preventDefault()
    const reviewDiv = ev.target.closest(".category-review"),
          anchor = reviewDiv.querySelector("a[data-action=toggle]")

    if (reviewDiv.hasAttribute("data-expanded")) {
      anchor.innerText = this.constructor.toggleText
      reviewDiv.removeAttribute("data-expanded")
    } else {
      anchor.innerText = "Collapse review"
      reviewDiv.dataset.expanded = ""
    }
  }

  renderComment(comment) {
    const fragment = document.createDocumentFragment()
    comment.split("\n").forEach(txt => {
      const p = document.createElement("p")
      p.innerText = txt
      fragment.appendChild(p)
    })
    return fragment
  }

  render() {
    const div = this.constructor.tmpl.content.querySelector("div").cloneNode(true)

    if (this.review.customerPhoto) {
      div.querySelector(".category-review__project [data-field=customer-avatar]").src = this.review.customerPhoto
    } else {
      div.querySelector(".category-review__project [data-field=customer-avatar]").remove()
    }
    div.querySelector(".category-review__project [data-field=customer-name] b").innerText = this.review.customerName
    div.querySelector(".category-review__project [data-field=project-name]").innerText = this.review.projectName
    div.querySelector(".category-review__project [data-field=rating]").appendChild((new Stars(this.review.rating)).render())
    div.querySelector(".category-review__project [data-field=date]").innerText = this.constructor.fmtUnix(this.review.createdAt)

    const imgContainer = div.querySelector(".category-review__photos"),
          lightboxGrp = `review-${this.review.id}`
    if (this.review.finalPhotos.length) {
      this.review.finalPhotos.forEach(photo => {
        const a = document.createElement("a"),
              img = document.createElement("img")
        a.setAttribute("href", photo.fullUrl)
        a.setAttribute("rel", lightboxGrp)
        a.setAttribute("data-lightbox", "")
        img.setAttribute("loading", "lazy")
        img.setAttribute("src", photo.smallUrl)
        img.setAttribute("alt", "")
        img.setAttribute("role", "presentation")
        a.appendChild(img)
        imgContainer.appendChild(a)
      })
    } else {
      imgContainer.remove()
    }

    if (this.review.comment) {
      div.querySelector(".category-review__details").prepend(this.renderComment(this.review.comment))
    }

    const subScoresDiv = div.querySelector(".category-review__scores")
    this.constructor.subScores.forEach(scoreDesc => {
      const scoreDiv = document.createElement("div"),
            scoreTitle = document.createElement("span"),
            scoreTitleB = document.createElement("b")
      scoreTitle.classList.add("txt--body-2")
      scoreTitleB.innerText = scoreDesc.title
      scoreTitle.append(scoreTitleB)
      scoreDiv.append(scoreTitle, (new Stars(this.review[scoreDesc.attr])).render())
      subScoresDiv.appendChild(scoreDiv)
    })

    div.querySelector("a[data-action=toggle]").addEventListener("click", this.onToggle)

    return div
  }
}

export class CategoryReviews {
  static selector = "[data-component=category-reviews]"

  constructor(el) {
    this.root = el
    this.render()
  }

  get reviewsData() {
    return JSON.parse(this.root.querySelector("script[type='application/json']").textContent)
  }

  render() {
    try {
      this.reviewsData.forEach(r => this.root.appendChild((new Review(r)).render()))
    } catch (ex) {
      Sentry.captureException(ex)
      this.root.replaceChildren(reviewsErr())
    }
  }
}

export class ReviewsApp {
  static selector = "[data-component=reviews-app]"

  static {
    this.filtersTmpl = document.createElement("template")
    this.filtersTmpl.innerHTML = `
    <div class="reviews-filters">
      <div class="content--58pct">
        <h3 class="mb--0">Filter</h3>
        <label>
          <span class="txt--menu-2">By rating</span>
          <select name="rating">
            <option value="">All</option>
            <option value="5">5 Star</option>
            <option value="4">4 Star</option>
            <option value="3">3 Star</option>
            <option value="2">2 Star</option>
            <option value="1">1 Star</option>
          </select>
        </label>
        <label>
          <span class="txt--menu-2">By category</span>
          <select name="category">
            <option value="">All</option>
            <option value="engagement">Engagement Rings</option>
            <option value="pendants">Pendants / Necklaces</option>
            <option value="statement">Statement Rings</option>
            <option value="signet">Signet Rings</option>
            <option value="class">Class Rings</option>
            <option value="bands">Wedding Rings / Bands</option>
            <option value="cufflinks">Cufflinks</option>
            <option value="bracelet">Bracelets</option>
            <option value="earrings">Earrings</option>
            <option value="lapelpin">Lapel Pins</option>
          </select>
        </label>
      </div>
    </div>
    `
  }

  static numFmt = new Intl.NumberFormat()

  constructor(el) {
    this.root = el
    this.initData()
    this.render()
    this.errMsg = null
    window.addEventListener("popstate", event => {
      if (event.state?.page && event.state?.filters) {
        this.curPage = event.state.page
        this.filters = event.state.filters
        this.loadResults()
      }
    })
  }

  initData() {
    try {
      const data = JSON.parse(this.root.querySelector("script[type='application/json']").textContent)
      this.filters = data.filters
      this.curPage = data.page
      this.numPages = data.numOfPages
      this.reviews = data.reviews
    } catch (ex) {
      Sentry.captureException(ex)
    }
  }

  loadResults = () => {
    const params = new URLSearchParams({page: this.curPage})
    if (this.filters.category) {
      params.set("category", this.filters.category)
    }
    if (this.filters.rating) {
      params.set("rating", this.filters.rating)
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.pushState({page: this.curPage, filters: this.filters}, "", newUrl)

    this.root.dataset.loading = true
    api.get(window.cm.urls["reviews-api"], params).then(resp => {
      this.errMsg = null
      this.curPage = resp.page
      this.numPages = resp.numOfPages
      this.filters = resp.filters
      this.reviews = resp.reviews
      this.render()
      this.root.scrollIntoView({block: "start", inline: "nearest", behavior: "smooth"})
    }).catch(err => {
      this.errMsg = apiErrorMessage(err)
      this.reviews = null
      this.render()
    }).finally(() => this.root.removeAttribute("data-loading"))
  }

  onClickPage = ev => {
    ev.preventDefault()
    const link = ev.target.tagName === "A" ? ev.target : ev.target.closest("a")
    this.curPage = link.dataset.page
    this.loadResults()
  }

  onFilter = ev => {
    this.filters[ev.target.name] = ev.target.value
    this.curPage = 1
    this.loadResults()
  }

  renderFilters = () => {
    const
      el = this.constructor.filtersTmpl.content.cloneNode(true),
      ratingSelect = el.querySelector("select[name=rating]"),
      categorySelect = el.querySelector("select[name=category]")

    ratingSelect.addEventListener("change", this.onFilter)
    if (this.filters.rating) {
      ratingSelect.value = this.filters.rating
    }
    categorySelect.addEventListener("change", this.onFilter)
    if (this.filters.category) {
      categorySelect.value = this.filters.category
    }

    return el
  }

  buildPageLink = page => {
    if (page === this.curPage) {
      const curPage = document.createElement("div")
      curPage.classList.add("reviews-pagination__page", "reviews-pagination__page--current")
      curPage.innerText = this.constructor.numFmt.format(this.curPage)
      curPage.setAttribute("aria-label", `Currently on page ${page}`)
      return curPage
    } else {
      const link = document.createElement("a")
      link.classList.add("reviews-pagination__page")
      link.href = "#"
      link.innerText = this.constructor.numFmt.format(page)
      link.dataset.page = page
      link.setAttribute("aria-label", `Go to page ${page}`)
      link.addEventListener("click", this.onClickPage)
      return link
    }
  }

  buildPageEllipsis = () => {
    const div = document.createElement("div")
    div.classList.add("reviews-pagination__page")
    div.innerText = "…"
    return div
  }

  renderPagination = () => {
    const nav = document.createElement("nav")
    nav.classList.add("reviews-pagination", "content--58pct", "txt--menu-2")

    if (this.curPage > 1) {
      const btn = document.createElement("a")
      btn.classList.add("btn", "btn--sm", "btn--left", "mr--0_5")
      btn.setAttribute("aria-label", "Previous page")
      btn.dataset.page = this.curPage - 1
      btn.addEventListener("click", this.onClickPage)
      nav.appendChild(btn)
    }

    // within first 5 pages, we show first 5 page nums, then ellipsis, then final page.
    // within last 5 pages, we show first page, then ellipsis, then last 5 pages.
    // in between, we show first, ellipsis, current & +/- 1, ellipsis, last
    if (this.curPage < 5) {
      for (let p = 1; p <= Math.min(5, this.numPages); p++) {
        nav.appendChild(this.buildPageLink(p))
      }
      if (this.numPages > 6) {
        nav.appendChild(this.buildPageEllipsis())
      }
      if (this.numPages > 5) {
        nav.appendChild(this.buildPageLink(this.numPages))
      }
    } else if (this.curPage >= (this.numPages - 4)) {
      nav.appendChild(this.buildPageLink(1))
      if (this.numPages > 6) {
        nav.appendChild(this.buildPageEllipsis())
      }
      for (let p = Math.max((this.numPages - 4), 2); p <= this.numPages; p++) {
        nav.appendChild(this.buildPageLink(p))
      }
    } else {
      nav.append(
        this.buildPageLink(1),
        this.buildPageEllipsis(),
        this.buildPageLink(this.curPage - 1),
        this.buildPageLink(this.curPage),
        this.buildPageLink(this.curPage + 1),
        this.buildPageEllipsis(),
        this.buildPageLink(this.numPages),
      )
    }

    if (this.curPage < this.numPages) {
      const btn = document.createElement("a")
      btn.classList.add("btn", "btn--sm", "btn--right", "ml--0_5")
      btn.setAttribute("aria-label", "Next page")
      btn.dataset.page = this.curPage + 1
      btn.addEventListener("click", this.onClickPage)
      nav.appendChild(btn)
    }

    return nav
  }

  renderReviewsList = () => {
    const div = document.createElement("div")
    div.classList.add("reviews-list", "content--58pct")
    div.append(...this.reviews.map(r => (new Review(r)).render()), this.renderPagination())
    return div
  }

  render() {
    if (!this.reviews) {
      const reloadBtnGrp = document.createElement("div")
      reloadBtnGrp.classList.add("btn-group", "btn-group--center", "mb--2")
      const btn = document.createElement("button")
      btn.classList.add("btn--lg", "btn--secondary", "btn--wide")
      btn.setAttribute("type", "button")
      btn.innerText = "Try again"
      btn.addEventListener("click", this.loadResults)
      reloadBtnGrp.appendChild(btn)
      this.root.replaceChildren(reviewsErr(this.errMsg), reloadBtnGrp)
    } else {
      this.root.replaceChildren(this.renderFilters(), this.renderReviewsList())
    }
  }
}

export class ReviewsBreakdown {
  static selector = "[data-component=reviews-breakdown]"
  static {
    this.tmpl = document.createElement("template")
    this.tmpl.innerHTML = `
      <h3>All reviews</h3>
      <div class="reviews-breakdown__aggregate">
        <div data-field="aggregate-stars"></div>
        <span class="txt--menu-1">
          <span data-field="aggregate-rating"></span>/5
        </span>
        <small>
          <span data-field="ratings-count"></span>
          reviews
        </small>
      </div>
    `
  }

  constructor(root) {
    this.root = root
    this.initData()

    this.render()
  }

  initData = () => {
    try {
      this.data = JSON.parse(this.root.querySelector("script[type='application/json']").textContent)
    } catch (ex) {
      Sentry.captureException(ex)
    }
  }

  buildBreakdownTable = () => {
    const tbl = document.createElement("table"), tbody = document.createElement("tbody")
    tbl.classList.add("reviews-breakdown__ratings")
    tbody.append(...this.data.breakdown.reverse().map(({rating, count}) => this.buildBreakdownRow(rating, count)))
    tbl.append(tbody)
    return tbl
  }

  buildBreakdownRow = (stars, ratingsCount) => {
    const
      row = document.createElement("tr"),
      title = document.createElement("span"),
      titleB = document.createElement("b"),
      bar = document.createElement("div"),
      count = document.createElement("span")

    titleB.innerText = `${stars} star`
    title.append(titleB)

    bar.classList.add("reviews-breakdown__bar")
    bar.style.setProperty("--pct", `${(ratingsCount / this.data.ratingsCount) * 100}%`)

    count.classList.add("txt--caption")
    count.innerText = ratingsCount.toLocaleString()

    row.classList.add("reviews-breakdown__row");  // semantically important ; for next line starting with [
    [title, bar, count].forEach(item => {
      const td = document.createElement("td")
      td.append(item)
      row.append(td)
    })

    return row
  }

  render() {
    try {
      const content = this.constructor.tmpl.content.cloneNode(true)

      content.querySelector("[data-field=aggregate-stars]").append((new Stars(Math.round(parseFloat(this.data.aggregateRating)))).render())
      content.querySelector("[data-field=aggregate-rating]").innerText = this.data.aggregateRating
      content.querySelector("[data-field=ratings-count]").innerText = this.data.ratingsCount.toLocaleString()
      content.append(this.buildBreakdownTable())

      this.root.append(content)
    } catch (ex) {
      Sentry.captureException(ex)
      throw ex
    }
  }
}

export class AggregateStars {
  static selector = "[data-component=aggregate-stars]"

  constructor(root) {
    this.root = root
    this.render()
  }

  render() {
    try {
      this.root.append((new Stars(Math.round(parseFloat(this.root.dataset.rating)))).render())
    } catch (ex) {
      Sentry.captureException(ex)
      throw ex
    }
  }
}
